import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  LayoutDashboard,
  Receipt,
  Calculator,
  MessageCircle,
  FileText,
  Building,
  Car,
  Users,
  BarChart3,
  Shield,
  Search,
  BookOpen,
  Lightbulb,
  HelpCircle,
  ArrowRight,
  Image,
  Package,
  Calendar,
  Building2,
  Rocket,
  CheckCircle,
  Star,
  Zap,
  Sparkles,
  Target,
  Wrench
} from 'lucide-react';
import OnboardingModal, { resetOnboarding } from '../components/OnboardingModal';

// Module documentation data
const modules = [
  {
    id: 'dashboard',
    name: 'Dashboard',
    icon: LayoutDashboard,
    path: '/dashboard',
    category: 'core',
    color: 'bg-emerald-100 text-emerald-600 border-emerald-200',
    description: 'Your tax health command center',
    fullDescription: 'The Dashboard provides a comprehensive overview of your financial health and tax situation. View real-time tax estimates, track savings, monitor year-over-year comparisons, and get AI-powered strategy tips.',
    features: [
      'Real-time estimated tax liability calculation',
      'Federal, State, and Self-Employment tax breakdown',
      'Year selector to view historical data (up to 7 years)',
      'Strategy Feed with AI-powered savings tips',
      'S-Corp comparison calculator',
      'Spending alerts and recommendations',
      'Year-over-Year comparison charts',
      'Quick action buttons for common tasks'
    ],
    tips: [
      'Check your Dashboard weekly to stay on top of your tax situation',
      'Use the Strategy Feed to discover new deduction opportunities',
      'Review the S-Corp Optimizer if your income exceeds $50,000'
    ]
  },
  {
    id: 'transactions',
    name: 'Ledger',
    icon: Receipt,
    path: '/transactions',
    category: 'core',
    color: 'bg-blue-100 text-blue-600 border-blue-200',
    description: 'Track all income and expenses',
    fullDescription: 'The Ledger is where you record all your business income and expenses. Use AI-powered categorization when importing CSVs, or manually add transactions. Each transaction can be marked as income, expense, or draft for review.',
    features: [
      'Add individual transactions manually',
      'Bulk import via CSV with AI categorization',
      'Filter by type (Income/Expense), status, and date',
      'AI confidence scoring for imported transactions',
      'Link transactions to receipts',
      'Export transactions to CSV',
      'Draft mode for unreviewed imports'
    ],
    tips: [
      'Import your bank statements monthly to stay current',
      'Review AI-categorized transactions for accuracy',
      'Use consistent categories for better reporting'
    ]
  },
  {
    id: 'bank-connections',
    name: 'Bank Connections',
    icon: Building2,
    path: '/bank-connections',
    category: 'core',
    color: 'bg-indigo-100 text-indigo-600 border-indigo-200',
    description: 'Connect bank accounts for automatic syncing',
    fullDescription: 'Link your bank accounts securely through Plaid to automatically import transactions. This eliminates manual data entry and helps you track potential deductions more completely.',
    features: [
      'Secure bank connection via Plaid',
      'Automatic transaction syncing',
      'Support for multiple accounts',
      'Real-time balance updates',
      'Transaction categorization'
    ],
    tips: [
      'Connect your primary business account first',
      'Review synced transactions weekly',
      'Disconnect accounts you no longer use'
    ]
  },
  {
    id: 'deductions',
    name: 'Deductions',
    icon: Calculator,
    path: '/deductions',
    category: 'core',
    color: 'bg-purple-100 text-purple-600 border-purple-200',
    description: 'Track tax-deductible expenses',
    fullDescription: 'The Deductions module helps you track and categorize all your tax-deductible business expenses. Organized by IRS categories, it ensures you maximize your deductions while staying compliant.',
    features: [
      'IRS-compliant expense categories',
      'Running total of deductions by category',
      'Link deductions to receipts and documents',
      'Notes and descriptions for audit protection',
      'Percentage deductions (home office, vehicle)'
    ],
    tips: [
      'Take photos of receipts immediately after purchases',
      'Review IRS guidelines for questionable deductions',
      'Keep detailed notes for large or unusual expenses'
    ]
  },
  {
    id: 'quarterly',
    name: 'Quarterly Estimates',
    icon: Calendar,
    path: '/quarterly',
    category: 'core',
    color: 'bg-orange-100 text-orange-600 border-orange-200',
    description: 'Manage quarterly tax payments',
    fullDescription: 'Stay on top of your quarterly estimated tax payments to avoid penalties. Track what you owe, what you\'ve paid, and upcoming deadlines.',
    features: [
      'Automatic quarterly payment calculations',
      'Payment tracking and history',
      'Due date reminders',
      'Safe harbor calculation',
      'Payment voucher generation'
    ],
    tips: [
      'Pay quarterly estimates by the 15th of the due month',
      'Use safe harbor rules to avoid underpayment penalties',
      'Adjust payments if income changes significantly'
    ]
  },
  {
    id: 'documents',
    name: 'Documents',
    icon: FileText,
    path: '/documents',
    category: 'tracking',
    color: 'bg-slate-100 text-slate-600 border-slate-200',
    description: 'Store important tax documents',
    fullDescription: 'Keep all your important tax documents organized and accessible. Store W-9s, contracts, tax forms, and other critical paperwork in one secure location. Use Quick OCR Preview to extract text from any document.',
    features: [
      'Secure document storage',
      'Multiple file format support (PDF, images, etc.)',
      'Quick OCR Preview for text extraction',
      'Inline category selection and editing',
      'Extraction status tracking',
      'Retry failed extractions',
      'Quick search functionality'
    ],
    tips: [
      'Upload documents as soon as you receive them',
      'Use Quick OCR Preview to verify document contents',
      'Change categories directly from the document list',
      'Retry failed extractions after fixing issues'
    ]
  },
  {
    id: 'receipts',
    name: 'Receipts',
    icon: Image,
    path: '/receipts',
    category: 'tracking',
    color: 'bg-pink-100 text-pink-600 border-pink-200',
    description: 'Receipt gallery with OCR',
    fullDescription: 'The Receipt Shoebox lets you capture and store receipt images. Our OCR technology automatically extracts vendor, amount, and date information, making it easy to link receipts to transactions.',
    features: [
      'Photo capture and upload',
      'OCR text extraction',
      'Automatic data parsing',
      'Link to transactions',
      'Gallery view with filtering',
      'Bulk upload support'
    ],
    tips: [
      'Take photos of receipts immediately - paper fades!',
      'Review OCR results for accuracy',
      'Link receipts to transactions for audit protection'
    ]
  },
  {
    id: 'mileage',
    name: 'Mileage Tracker',
    icon: Car,
    path: '/mileage',
    category: 'tracking',
    color: 'bg-cyan-100 text-cyan-600 border-cyan-200',
    description: 'Log business miles for deductions',
    fullDescription: 'Track your business mileage for vehicle deductions. Log trips manually or use our tracking features. Calculate deductions using either the standard mileage rate or actual expenses method.',
    features: [
      'Trip logging with purpose notes',
      'Standard mileage rate calculations',
      'Actual expense tracking option',
      'Business vs personal separation',
      'Mileage reports for tax filing',
      'Year-end summaries'
    ],
    tips: [
      'Log trips immediately after driving',
      'Include the business purpose for each trip',
      'Compare standard vs actual expense methods annually'
    ]
  },
  {
    id: 'contractors',
    name: '1099 Contractors',
    icon: Users,
    path: '/contractors',
    category: 'tracking',
    color: 'bg-indigo-100 text-indigo-600 border-indigo-200',
    description: 'Track contractor payments',
    fullDescription: 'Manage your independent contractor relationships and payments. Track payments throughout the year to simplify 1099 filing at tax time.',
    features: [
      'Contractor profile management',
      'W-9 collection and storage',
      'Payment tracking by contractor',
      '1099 threshold monitoring ($600)',
      'Year-end 1099 preparation',
      'Payment history reports'
    ],
    tips: [
      'Collect W-9s before making first payment',
      'Track all payments, even small ones',
      'Review totals in December for 1099 filing'
    ]
  },
  {
    id: 'reports',
    name: 'Reports',
    icon: BarChart3,
    path: '/reports',
    category: 'reports',
    color: 'bg-green-100 text-green-600 border-green-200',
    description: 'Financial reports and analytics',
    fullDescription: 'Generate detailed financial reports for analysis, tax preparation, or sharing with your CPA. View income statements, expense breakdowns, and trend analysis.',
    features: [
      'Profit & Loss statements',
      'Expense breakdown by category',
      'Income analysis',
      'Trend charts and graphs',
      'Export to PDF/CSV',
      'Custom date ranges'
    ],
    tips: [
      'Generate monthly P&L reports to track business health',
      'Share reports with your CPA before tax season',
      'Use trend analysis to identify expense patterns'
    ]
  },
  {
    id: 'tax-package',
    name: 'Tax Package',
    icon: Package,
    path: '/tax-package',
    category: 'reports',
    color: 'bg-red-100 text-red-600 border-red-200',
    description: 'Generate CPA-ready tax packages',
    fullDescription: 'Create comprehensive tax packages ready for your CPA or tax preparer. Includes Schedule C generation, income summaries, expense reports, and all supporting materials. Download as PDF or email directly to your CPA.',
    features: [
      'Schedule C line item generation',
      'Comprehensive financial summary',
      'Self-employment tax calculations',
      'Audit risk analysis',
      'PDF export for download',
      'Email directly to CPA',
      'Year-over-year comparison',
      'Quarterly payment suggestions'
    ],
    tips: [
      'Generate your tax package in early January',
      'Review the audit risk section before filing',
      'Email directly to your CPA for faster turnaround',
      'Download PDF as backup documentation'
    ]
  },
  {
    id: 'businesses',
    name: 'Businesses',
    icon: Building,
    path: '/businesses',
    category: 'management',
    color: 'bg-amber-100 text-amber-600 border-amber-200',
    description: 'Manage multiple business entities',
    fullDescription: 'If you operate multiple businesses or have different entity types, manage them all in one place. Switch between businesses to view their individual financials.',
    features: [
      'Multiple business support',
      'Entity type tracking (LLC, S-Corp, Sole Prop)',
      'Per-business reporting',
      'EIN management',
      'Business switching'
    ],
    tips: [
      'Set up each business entity separately',
      'Keep finances strictly separated between entities',
      'Review entity structure annually with your CPA'
    ]
  },
  {
    id: 'chat',
    name: 'AI Advisors',
    icon: MessageCircle,
    path: '/chat',
    category: 'ai',
    color: 'bg-orange-100 text-orange-600 border-orange-200',
    description: 'Tax strategy chat with AI experts',
    fullDescription: 'Chat with AI-powered tax advisors who understand your financial situation. Get personalized recommendations for deductions, entity structure, retirement planning, and more.',
    features: [
      'Multiple AI advisor personas',
      'Context-aware advice based on your data',
      'S-Corp election guidance',
      'Deduction optimization suggestions',
      'Retirement planning advice',
      'Audit preparation help'
    ],
    tips: [
      'Be specific in your questions for better answers',
      'Ask follow-up questions to dig deeper',
      'Review AI suggestions with a real CPA for major decisions'
    ]
  },
  {
    id: 'strategy',
    name: 'Strategy',
    icon: Sparkles,
    path: '/strategy',
    category: 'ai',
    color: 'bg-purple-100 text-purple-600 border-purple-200',
    description: 'AI-powered tax strategy recommendations',
    fullDescription: 'Get personalized tax-saving strategies based on your financial data. View upcoming deadlines, set reminders, and get AI-generated insights to optimize your tax situation.',
    features: [
      'Personalized tax-saving recommendations',
      'Year selector to analyze different tax years',
      'Tax Calendar with upcoming deadlines',
      'Set reminders for important dates',
      'AI-powered financial analysis',
      'Potential savings calculations',
      'Ask AI Advisor about any strategy'
    ],
    tips: [
      'Review strategy recommendations monthly',
      'Set reminders for all quarterly tax deadlines',
      'Use the AI Analysis feature before major financial decisions',
      'Link strategies to AI Advisor for detailed guidance'
    ]
  },
  {
    id: 'audit-report',
    name: 'Audit Report',
    icon: Shield,
    path: '/audit-report',
    category: 'reports',
    color: 'bg-teal-100 text-teal-600 border-teal-200',
    description: 'Audit protection documentation',
    fullDescription: 'Generate comprehensive audit protection reports that document your income, expenses, and deductions with supporting evidence. Be prepared if the IRS comes knocking.',
    features: [
      'Comprehensive audit trail',
      'Receipt and document linking',
      'Category verification',
      'Year-by-year summaries',
      'Export for legal purposes'
    ],
    tips: [
      'Generate audit reports annually as a backup',
      'Ensure all major deductions have supporting docs',
      'Keep audit reports for at least 7 years'
    ]
  },
  {
    id: 'settings',
    name: 'Settings',
    icon: Wrench,
    path: '/settings',
    category: 'management',
    color: 'bg-gray-100 text-gray-600 border-gray-200',
    description: 'Configure your account',
    fullDescription: 'Manage your account settings including tax filing status, state of residence, spouse information, and notification preferences.',
    features: [
      'Filing status selection',
      'State of residence',
      'Spouse information for MFJ',
      'Notification preferences',
      'Account management'
    ],
    tips: [
      'Update filing status if your situation changes',
      'Set your state for accurate tax calculations',
      'Review settings at the start of each tax year'
    ]
  }
];

const categories = [
  { id: 'all', name: 'All Modules', icon: LayoutDashboard },
  { id: 'core', name: 'Core Features', icon: Star },
  { id: 'tracking', name: 'Tracking', icon: FileText },
  { id: 'reports', name: 'Reports', icon: BarChart3 },
  { id: 'ai', name: 'AI Tools', icon: Sparkles },
  { id: 'management', name: 'Management', icon: Wrench }
];

// FAQ keys - translations loaded in component
const faqKeys = [
  { q: 'faq_import_q', a: 'faq_import_a' },
  { q: 'faq_deduction_q', a: 'faq_deduction_a' },
  { q: 'faq_accuracy_q', a: 'faq_accuracy_a' },
  { q: 'faq_scorp_q', a: 'faq_scorp_a' },
  { q: 'faq_connect_q', a: 'faq_connect_a' },
  { q: 'faq_receipts_q', a: 'faq_receipts_a' },
  { q: 'faq_quarterly_q', a: 'faq_quarterly_a' }
];

// Onboarding step title translations
const stepTitleTranslations = {
  'Complete Your Profile': 'onboarding.complete_your_profile',
  'Explore Your Dashboard': 'onboarding.explore_dashboard',
  'Upload Your Documents': 'onboarding.upload_documents',
  'Manage Your Ledger': 'onboarding.manage_ledger',
  'Connect Your Bank (Optional)': 'onboarding.connect_bank'
};

const Help = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Build FAQs with translations
  const faqs = faqKeys.map(faq => ({
    question: t(`help.${faq.q}`),
    answer: t(`help.${faq.a}`)
  }));

  const filteredModules = modules.filter(module => {
    const matchesSearch = 
      module.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      module.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      module.fullDescription.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || module.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const handleRestartTour = () => {
    resetOnboarding();
    setShowOnboarding(true);
  };

  // Translated category names
  const translatedCategories = [
    { id: 'all', name: t('help.all_modules'), icon: LayoutDashboard },
    { id: 'core', name: t('help.core_features'), icon: Star },
    { id: 'tracking', name: t('help.tracking'), icon: FileText },
    { id: 'reports', name: t('help.reports'), icon: BarChart3 },
    { id: 'ai', name: t('help.ai_tools'), icon: Sparkles },
    { id: 'management', name: t('help.management'), icon: Wrench }
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="help-page">
        {/* Onboarding Modal */}
        {showOnboarding && <OnboardingModal forceShow={true} />}

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-1 flex items-center gap-2 font-heading text-stone-900">
              <BookOpen className="w-8 h-8 text-emerald-600" />
              {t('help.title')}
            </h1>
            <p className="text-stone-600">{t('help.subtitle')}</p>
          </div>
          <Button onClick={handleRestartTour} className="bg-emerald-600 hover:bg-emerald-700" data-testid="restart-tour-btn">
            <Rocket className="w-4 h-4 mr-2" />
            {t('help.restart_tour')}
          </Button>
        </div>

        {/* Quick Start Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200 hover:shadow-md transition-shadow cursor-pointer rounded-2xl" onClick={() => navigate('/transactions')}>
            <CardContent className="p-5">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-500 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-emerald-800">{t('help.quick_start')}</h3>
                  <p className="text-sm text-emerald-600">{t('help.add_first_transaction')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200 hover:shadow-md transition-shadow cursor-pointer rounded-2xl" onClick={() => navigate('/chat')}>
            <CardContent className="p-5">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center">
                  <MessageCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-blue-800">{t('help.ask_ai')}</h3>
                  <p className="text-sm text-blue-600">{t('help.get_personalized_advice')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200 hover:shadow-md transition-shadow cursor-pointer rounded-2xl" onClick={() => navigate('/settings')}>
            <CardContent className="p-5">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-purple-500 flex items-center justify-center">
                  <Wrench className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-purple-800">{t('help.setup')}</h3>
                  <p className="text-sm text-purple-600">{t('help.configure_profile')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filter */}
        <Card className="bg-white rounded-2xl border border-stone-200 shadow-soft">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  placeholder={t('help.search_placeholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="help-search-input"
                />
              </div>
              <div className="flex gap-2 flex-wrap">
                {translatedCategories.map(cat => (
                  <Button
                    key={cat.id}
                    variant={selectedCategory === cat.id ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedCategory(cat.id)}
                    className={selectedCategory === cat.id ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                    data-testid={`category-${cat.id}`}
                  >
                    <cat.icon className="w-4 h-4 mr-1" />
                    {cat.name}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Module Documentation */}
        <Tabs defaultValue="modules" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="modules" data-testid="tab-modules">
              <BookOpen className="w-4 h-4 mr-2" />
              {t('help.modules')}
            </TabsTrigger>
            <TabsTrigger value="faq" data-testid="tab-faq">
              <HelpCircle className="w-4 h-4 mr-2" />
              {t('help.faq')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="modules" className="space-y-4">
            {filteredModules.length === 0 ? (
              <Card className="bg-white rounded-2xl border border-stone-200 shadow-soft">
                <CardContent className="p-8 text-center">
                  <Search className="w-12 h-12 mx-auto mb-4 text-stone-300" />
                  <p className="text-stone-600">{t('help.no_modules_found')} "{searchQuery}"</p>
                  <Button variant="link" onClick={() => setSearchQuery('')}>{t('help.clear_search')}</Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {filteredModules.map(module => (
                  <ModuleCard key={module.id} module={module} navigate={navigate} t={t} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="faq">
            <Card className="bg-white rounded-2xl border border-stone-200 shadow-soft">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-heading text-stone-900">
                  <HelpCircle className="w-5 h-5 text-emerald-600" />
                  Frequently Asked Questions
                </CardTitle>
                <CardDescription>Quick answers to common questions</CardDescription>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  {faqs.map((faq, index) => (
                    <AccordionItem key={index} value={`faq-${index}`}>
                      <AccordionTrigger className="text-left" data-testid={`faq-${index}`}>
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-stone-600">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Footer Help */}
        <Card className="bg-stone-50 border-stone-200 rounded-2xl">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Lightbulb className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-stone-800">{t('help.need_more_help')}</h3>
                  <p className="text-sm text-stone-600">{t('help.chat_with_advisor')}</p>
                </div>
              </div>
              <Button onClick={() => navigate('/chat')} className="bg-emerald-600 hover:bg-emerald-700">
                <MessageCircle className="w-4 h-4 mr-2" />
                {t('help.chat_with_ai_advisor')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

// Module Card Component
const ModuleCard = ({ module, navigate, t }) => {
  const Icon = module.icon;
  const [expanded, setExpanded] = useState(false);

  return (
    <Card className="hover:shadow-md transition-all bg-white rounded-2xl border border-stone-200 shadow-soft" data-testid={`module-card-${module.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className={`p-3 rounded-xl border ${module.color}`}>
              <Icon className="w-6 h-6" />
            </div>
            <div>
              <CardTitle className="text-lg font-heading text-stone-900">{module.name}</CardTitle>
              <CardDescription>{module.description}</CardDescription>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(module.path)}
            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
            data-testid={`go-to-${module.id}`}
          >
            {t('help.open')}
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <p className="text-sm text-stone-600 mb-4">{module.fullDescription}</p>
        
        <div className="space-y-3">
          <div>
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-sm font-medium text-stone-700 flex items-center gap-1 hover:text-emerald-600"
            >
              <CheckCircle className="w-4 h-4" />
              {t('help.key_features')} ({module.features.length})
              <ArrowRight className={`w-3 h-3 transition-transform ${expanded ? 'rotate-90' : ''}`} />
            </button>
            {expanded && (
              <ul className="mt-2 space-y-1 pl-5">
                {module.features.map((feature, idx) => (
                  <li key={idx} className="text-sm text-stone-600 list-disc">{feature}</li>
                ))}
              </ul>
            )}
          </div>
          
          <div className="pt-3 border-t">
            <p className="text-xs font-medium text-amber-700 mb-2 flex items-center gap-1">
              <Lightbulb className="w-3 h-3" />
              {t('help.pro_tips')}
            </p>
            <ul className="space-y-1">
              {module.tips.slice(0, 2).map((tip, idx) => (
                <li key={idx} className="text-xs text-slate-500 flex items-start gap-2">
                  <span className="text-amber-500">•</span>
                  {tip}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default Help;
